(() => {
  if (window.__valoraieProductReviewsGoogle) return;
  window.__valoraieProductReviewsGoogle = true;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeDrawer = null;
  let openingControl = null;
  let activeLightbox = null;
  let lightboxTrigger = null;
  let lightboxImages = [];
  let lightboxIndex = 0;
  let lightboxTouchStart = null;
  const selectedImages = new WeakMap();

  const drawerPanel = (drawer) => drawer?.querySelector('.product-review-drawer__panel');

  function selectReviewTab(control, shouldFocus = false) {
    const section = control?.closest('[data-product-reviews]');
    const target = control?.dataset.reviewTab;
    if (!section || !target) return;
    section.querySelectorAll('[data-review-tab]').forEach((tab) => {
      const selected = tab === control;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    section.querySelectorAll('[data-review-panel]').forEach((panel) => { panel.hidden = panel.dataset.reviewPanel !== target; });
    if (shouldFocus) control.focus({ preventScroll: true });
  }

  function openDrawer(drawer, control) {
    if (!drawer || !drawer.hidden) return;
    if (activeDrawer) closeDrawer(activeDrawer, false);
    activeDrawer = drawer;
    openingControl = control || null;
    drawer.hidden = false;
    drawer.querySelectorAll('[data-review-form-started]').forEach((field) => { field.value = String(Date.now()); });
    document.documentElement.classList.add('valoraie-review-drawer-open');
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawerPanel(drawer)?.focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer = activeDrawer, restoreFocus = true) {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    const finish = () => {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', finish);
      if (drawer === activeDrawer) activeDrawer = null;
      if (!document.querySelector('.product-review-drawer.is-open')) {
        document.documentElement.classList.remove('valoraie-review-drawer-open');
      }
      if (restoreFocus && openingControl?.isConnected) openingControl.focus({ preventScroll: true });
      openingControl = null;
    };
    drawer.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 380);
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || (!activeDrawer && !activeLightbox)) return;
    const panel = activeLightbox?.querySelector('[data-review-image-panel]') || drawerPanel(activeDrawer);
    const focusable = Array.from(panel?.querySelectorAll(focusableSelector) || [])
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      panel?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function showLightboxImage(index) {
    if (!activeLightbox || !lightboxImages.length) return;
    lightboxIndex = (index + lightboxImages.length) % lightboxImages.length;
    const current = lightboxImages[lightboxIndex];
    const image = activeLightbox.querySelector('[data-review-lightbox-image]');
    const counter = activeLightbox.querySelector('[data-review-lightbox-counter]');
    image.src = current.src;
    image.alt = current.alt;
    counter.textContent = lightboxImages.length > 1 ? `${lightboxIndex + 1} / ${lightboxImages.length}` : '';
    activeLightbox.querySelectorAll('.product-review-lightbox__nav').forEach((control) => { control.hidden = lightboxImages.length < 2; });
  }

  function openImageLightbox(control) {
    const section = control.closest('[data-product-reviews]');
    const lightbox = section?.querySelector('[data-review-image-lightbox]');
    const gallery = control.closest('[data-review-image-gallery]');
    if (!lightbox || !gallery) return;
    const controls = Array.from(gallery.querySelectorAll('[data-review-image-open]'));
    lightboxImages = controls.map((item) => ({
      src: item.dataset.reviewImageSrc || item.getAttribute('href') || item.querySelector('img')?.src || '',
      alt: item.dataset.reviewImageAlt || item.querySelector('img')?.alt || 'Customer review photo'
    })).filter((item) => item.src);
    lightboxIndex = Math.max(0, controls.indexOf(control));
    activeLightbox = lightbox;
    lightboxTrigger = control;
    lightbox.hidden = false;
    showLightboxImage(lightboxIndex);
    document.documentElement.classList.add('valoraie-review-lightbox-open');
    window.requestAnimationFrame(() => {
      lightbox.classList.add('is-open');
      lightbox.querySelector('.product-review-lightbox__close')?.focus({ preventScroll: true });
    });
  }

  function closeImageLightbox(restoreFocus = true) {
    const lightbox = activeLightbox;
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    const finish = () => {
      lightbox.hidden = true;
      lightbox.removeEventListener('transitionend', finish);
      const image = lightbox.querySelector('[data-review-lightbox-image]');
      image.removeAttribute('src');
      document.documentElement.classList.remove('valoraie-review-lightbox-open');
      activeLightbox = null;
      lightboxImages = [];
      if (restoreFocus && lightboxTrigger?.isConnected) lightboxTrigger.focus({ preventScroll: true });
      lightboxTrigger = null;
    };
    lightbox.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 280);
  }

  function clearImageSelection(form) {
    const entries = selectedImages.get(form) || [];
    entries.forEach((entry) => URL.revokeObjectURL(entry.preview));
    selectedImages.delete(form);
    const input = form.querySelector('[data-review-image-input]');
    if (input) input.value = '';
    const previews = form.querySelector('[data-review-image-previews]');
    if (previews) previews.replaceChildren();
  }

  function renderImageSelection(form) {
    const previews = form.querySelector('[data-review-image-previews]');
    if (!previews) return;
    previews.replaceChildren();
    (selectedImages.get(form) || []).forEach((entry, index) => {
      const item = document.createElement('div');
      item.className = 'product-review-form__photo-preview';
      const image = document.createElement('img');
      image.src = entry.preview;
      image.alt = `Selected review photo ${index + 1}`;
      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'product-review-form__photo-open';
      open.dataset.reviewImageOpen = '';
      open.dataset.reviewImageSrc = entry.preview;
      open.dataset.reviewImageAlt = image.alt;
      open.setAttribute('aria-label', `Open selected photo ${index + 1} in image viewer`);
      open.append(image);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'product-review-form__photo-remove';
      remove.dataset.reviewImageRemove = String(index);
      remove.setAttribute('aria-label', `Remove photo ${index + 1}`);
      remove.textContent = '×';
      item.append(open, remove);
      previews.append(item);
    });
  }

  function fileData(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(new Error('We could not read one of the selected photos.'));
      reader.readAsDataURL(file);
    });
  }

  async function decodeReviewImage(file) {
    if ('createImageBitmap' in window) return createImageBitmap(file);
    const source = URL.createObjectURL(file);
    try {
      const image = await new Promise((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('We could not process one of the selected photos.'));
        element.src = source;
      });
      return image;
    } finally {
      URL.revokeObjectURL(source);
    }
  }

  async function compressReviewImage(file) {
    const supported = ['image/jpeg', 'image/png', 'image/webp'];
    if (!supported.includes(file.type)) throw new Error('Please choose JPG, PNG or WebP photos.');
    const bitmap = await decodeReviewImage(file);
    let width = bitmap.width;
    let height = bitmap.height;
    const maxDimension = 1600;
    if (Math.max(width, height) > maxDimension) {
      const scale = maxDimension / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    let quality = .82;
    let blob;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
      if (blob && blob.type !== 'image/webp') blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob?.size <= 750000) break;
      quality -= .12;
    }
    if (!blob || blob.size > 825000) throw new Error('One photo is too large. Please choose a smaller image.');
    const extension = blob.type === 'image/webp' ? '.webp' : '.jpg';
    return { name: file.name.replace(/\.[^.]+$/, extension), type: blob.type, data: await fileData(blob) };
  }

  document.addEventListener('click', (event) => {
    const reviewTab = event.target.closest('[data-review-tab]');
    if (reviewTab) {
      selectReviewTab(reviewTab);
      return;
    }

    const imageOpener = event.target.closest('[data-review-image-open]');
    if (imageOpener) {
      event.preventDefault();
      openImageLightbox(imageOpener);
      return;
    }

    if (event.target.closest('[data-review-image-close]')) {
      closeImageLightbox();
      return;
    }

    if (event.target.closest('[data-review-image-previous]')) {
      showLightboxImage(lightboxIndex - 1);
      return;
    }

    if (event.target.closest('[data-review-image-next]')) {
      showLightboxImage(lightboxIndex + 1);
      return;
    }

    const opener = event.target.closest('[data-review-drawer-open]');
    if (opener) {
      const section = opener.closest('[data-product-reviews]');
      const drawer = section?.querySelector(`[data-review-drawer="${opener.dataset.reviewDrawerOpen}"]`);
      openDrawer(drawer, opener);
      return;
    }

    const closer = event.target.closest('[data-review-drawer-close]');
    if (closer) closeDrawer(closer.closest('[data-review-drawer]'));

    const removeImage = event.target.closest('[data-review-image-remove]');
    if (removeImage) {
      const form = removeImage.closest('form');
      const entries = selectedImages.get(form) || [];
      const [removed] = entries.splice(Number(removeImage.dataset.reviewImageRemove), 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      selectedImages.set(form, entries);
      renderImageSelection(form);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-review-image-input]');
    if (!input) return;
    const form = input.closest('form');
    const files = Array.from(input.files || []).slice(0, 3);
    clearImageSelection(form);
    selectedImages.set(form, files.map((file) => ({ file, preview: URL.createObjectURL(file) })));
    renderImageSelection(form);
    const status = form.querySelector('[data-review-form-status]');
    if (status && (input.files?.length || 0) > 3) status.textContent = 'You can upload up to 3 photos. Only the first 3 were selected.';
  });

  document.addEventListener('keydown', (event) => {
    const reviewTab = event.target.closest('[data-review-tab]');
    if (reviewTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = Array.from(reviewTab.closest('[role="tablist"]')?.querySelectorAll('[data-review-tab]') || []);
      if (!tabs.length) return;
      event.preventDefault();
      const current = tabs.indexOf(reviewTab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      selectReviewTab(tabs[next], true);
      return;
    }
    if (event.key === 'Escape' && activeLightbox) {
      event.preventDefault();
      closeImageLightbox();
      return;
    }
    if (event.key === 'ArrowLeft' && activeLightbox) {
      event.preventDefault();
      showLightboxImage(lightboxIndex - 1);
      return;
    }
    if (event.key === 'ArrowRight' && activeLightbox) {
      event.preventDefault();
      showLightboxImage(lightboxIndex + 1);
      return;
    }
    if (event.key === 'Escape' && activeDrawer) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    trapFocus(event);
  });

  document.addEventListener('touchstart', (event) => {
    if (!activeLightbox || !event.target.closest('[data-review-image-panel]')) return;
    lightboxTouchStart = event.changedTouches[0]?.clientX ?? null;
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    if (!activeLightbox || lightboxTouchStart === null || !event.target.closest('[data-review-image-panel]')) return;
    const distance = (event.changedTouches[0]?.clientX ?? lightboxTouchStart) - lightboxTouchStart;
    lightboxTouchStart = null;
    if (Math.abs(distance) < 45) return;
    showLightboxImage(lightboxIndex + (distance < 0 ? 1 : -1));
  }, { passive: true });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.product-review-form');
    const endpoint = form?.closest('[data-product-reviews]')?.dataset.reviewServiceEndpoint;
    if (!form || !endpoint) return;

    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-review-form-status]');
    submit?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';

    try {
      const requestData = Object.fromEntries(new FormData(form));
      const images = selectedImages.get(form) || [];
      if (images.length) {
        if (status) status.textContent = `Preparing ${images.length} photo${images.length === 1 ? '' : 's'}…`;
        requestData['contact[review_images]'] = await Promise.all(images.map(({ file }) => compressReviewImage(file)));
      }
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      form.reset();
      clearImageSelection(form);
      if (status) status.textContent = form.querySelector('[name="contact[submission_type]"]')?.value === 'Product review'
        ? 'Thank you. Your review has been submitted for moderation.'
        : 'Thank you. Your question has been sent.';
      status?.focus?.();
    } catch (error) {
      if (status) status.textContent = error.message || 'We could not send this just now. Please try again.';
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  });
})();
